/**
 * Document `form-action` allows `'self'` plus these `https://*.suffix` hosts
 * (see `CSP_DIRECTIVES['form-action']` in lib/security-headers.mjs). A POST
 * from the app shell to any other origin is blocked.
 */
export const DOCUMENT_FORM_ACTION_HOST_SUFFIXES = [
  'timepilot.tranquility.ai',
  'timepilot.knox.tranquility.ai',
  'timepilot.knox.tranquility-ai2.com',
  'timepilot.knox.tranquilitysi.com',
  'timepilot.knox.tranquility-ai-sandbox.com',
  'amazoncognito.com',
  'intercom.io',
] as const;

/**
 * Off-platform integration host suffixes. They are not on the app-shell
 * form-action list.
 *
 * A suffix matches the host itself and any subdomain (`sockpuppet.io`,
 * `timepilot.portal.sockpuppet.io`), https only, default port only, no
 * credentials. The vendor runs a portal host per customer and renames them, so
 * pinning exact origins here means a flag-config change ships as a code change.
 *
 * Widening a suffix does NOT widen any handoff document: that response's
 * `form-action` is the single concrete origin the click resolved to (see
 * buildIntegrationHandoffDocument), never the wildcard. What a suffix widens is
 * which origins a tenant's feature flag may name — so keep these scoped to one
 * vendor domain. Everything under a suffix can receive the handoff fields
 * (`tenant`, `case`, `user`, and any flag `params`, including a presigned
 * `s3_url`). `auth_token` is still never attached off our own origins — see
 * isOwnOrigin in components/uploaders-menu.tsx.
 */
export const EXTERNAL_INTEGRATION_FORM_HOST_SUFFIXES = [
  'sockpuppet.io',
] as const;

export const INTEGRATION_HANDOFF_PATH = '/api/integrations/external-handoff';
export const INTEGRATION_HANDOFF_ACTION_FIELD = '_tpl_action';

function hostMatchesSuffix(hostname: string, suffix: string): boolean {
  return hostname === suffix || hostname.endsWith(`.${suffix}`);
}

function parseAction(action: string, selfOrigin: string): URL | null {
  try {
    return new URL(action, selfOrigin);
  } catch {
    return null;
  }
}

/** True when the app shell's enforced form-action list already allows `action`. */
export function integrationFormActionAllowed(action: string, selfOrigin: string): boolean {
  const url = parseAction(action, selfOrigin);
  if (!url) return false;
  let self: URL;
  try {
    self = new URL(selfOrigin);
  } catch {
    return false;
  }
  if (url.origin === self.origin) return true;
  if (url.protocol !== 'https:') return false;
  return DOCUMENT_FORM_ACTION_HOST_SUFFIXES.some((suffix) => hostMatchesSuffix(url.hostname, suffix));
}

/**
 * Origin the handoff may POST to, or null when `action` is not an allowlisted
 * external integration URL. Always a single concrete origin taken from `action`
 * itself — the suffix list decides whether, not what.
 */
export function externalIntegrationHandoffOrigin(action: string): string | null {
  let url: URL;
  try {
    url = new URL(action);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username || url.password) return null;
  // A port would travel into the response CSP with the origin. The vendor
  // portals are 443; reject anything else rather than widen the policy.
  if (url.port) return null;
  const allowed = EXTERNAL_INTEGRATION_FORM_HOST_SUFFIXES.some(
    (suffix) => hostMatchesSuffix(url.hostname, suffix),
  );
  return allowed ? url.origin : null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function isSafeHandoffFieldName(name: string): boolean {
  return name !== INTEGRATION_HANDOFF_ACTION_FIELD && /^[A-Za-z0-9_.:-]{1,128}$/.test(name);
}

/** HTML document that POSTs `fields` to `action`, plus a CSP that allows only that origin. */
export function buildIntegrationHandoffDocument(
  action: string,
  fields: Record<string, string>,
  nonce: string,
): { html: string; csp: string } | null {
  const origin = externalIntegrationHandoffOrigin(action);
  if (!origin || !/^[A-Za-z0-9+/=_-]+$/.test(nonce)) return null;
  const inputs = Object.entries(fields)
    .filter(([name]) => isSafeHandoffFieldName(name))
    .map(([name, value]) => (
      `<input type="hidden" name="${escapeHtml(name)}" value="${escapeHtml(value)}">`
    ))
    .join('');
  const html = `<!DOCTYPE html><html><body><form id="tpl-handoff" method="POST" action="${escapeHtml(action)}">${inputs}</form><script nonce="${nonce}">document.getElementById('tpl-handoff').submit()</script></body></html>`;
  const csp = [
    "default-src 'none'",
    `form-action ${origin}`,
    `script-src 'nonce-${nonce}'`,
    "base-uri 'none'",
    "frame-ancestors 'none'",
  ].join('; ');
  return { html, csp };
}
